import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { initTyro, resetTyroSdk } from "../lib/tyroService";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";
import { CreditCard, Spinner, CheckCircle, XCircle, WarningCircle, ArrowCounterClockwise, X } from "@phosphor-icons/react";
import SaleCompleteCard from "./SaleCompleteCard";

/**
 * Live-status modal for Tyro EFTPOS purchases & refunds.
 *
 * Consumer flow:
 *   <TyroPaymentDialog
 *     mode="purchase"            // "purchase" | "refund"
 *     amountCents={19900}
 *     originalTxRef="TYRO-..."   // required for refund only
 *     onApproved={(meta) => createSale(meta)}
 *     onClose={() => setOpen(false)}
 *   />
 *
 * The dialog handles the entire SDK lifecycle:
 *   1. Loads the iClient JS for the store's configured `tyro_env`.
 *   2. Shows live `statusMessageCallback` updates ("Swipe card · Approving").
 *   3. Pops the merchant `questionCallback` ("Signature OK?") inline.
 *   4. On APPROVED → fires `onApproved(tyroMeta)` and auto-closes.
 *   5. On DECLINED / CANCELLED / SYSTEM ERROR → red error state with retry.
 *   6. On UNKNOWN (network blip) → bold warning to check the physical terminal.
 *
 * The component is intentionally self-contained — no Redux, no global state.
 * It owns the SDK instance for the duration of its mount and tears down on
 * close so re-opening for a new amount starts a clean transaction.
 */
const STATE = {
  IDLE: "idle",
  RUNNING: "running",
  APPROVED: "approved",
  SAVED: "saved",        // post-approval: sale persisted, action panel showing
  DECLINED: "declined",
  UNKNOWN: "unknown",
};

export default function TyroPaymentDialog({
  mode = "purchase",
  amountCents,
  originalTxRef = "",
  onApproved,
  onClose,
  // ── Post-approval morph (optional) ─────────────────────────────
  // When the parent supplies `completedSale` AFTER `onApproved` has
  // resolved the sale persistence step, the dialog smoothly morphs
  // its body into the unified SaleCompleteCard action panel (Print /
  // SMS / Email / Print label / SMS Google Review / Done) —
  // eliminating the second pop-up modal that used to appear after
  // approval. Pass `null` (or omit) to keep the legacy auto-close
  // behaviour for callers that don't want the morph.
  completedSale = null,
  // After an approved refund POSTs back to the backend, the parent
  // (SaleDetail's refund flow) can pass the persisted refund payload
  // here to trigger the same APPROVED → SAVED morph as a purchase —
  // only this time the body renders in RED ("Refunded · $X.XX
  // returned" pill) so the cashier and customer get a clear visual
  // confirmation the money's heading the other way.
  completedRefund = null,
  customer = null,
  repairTicket = null,
  storeForActions = null,
  onPrintReceipt,
  onPrintLabel,
  onViewInvoice,
  // When the sale/payment POST fails AFTER the card was charged, the parent
  // passes the error message here so the dialog shows a recovery panel
  // (Retry saving / Close) instead of hanging on "Saving sale…" forever.
  saveError = null,
  onRetrySave,
}) {
  // ── Defensive amount guard ────────────────────────────────────────
  // Refuse to engage the SDK for $0.00 / NaN / negative amounts. Catches
  // any upstream caller that opens us before the cart total is
  // computed, or after the cart was cleared by a race condition.
  // Without this the dialog would happily simulate-approve a $0
  // transaction and the green Approved screen would show "$0.00
  // charged" — confusing for cashier + cardholder.
  //
  // We can't early-return here (React rules-of-hooks — all hooks must
  // run on every render of the same component). Instead a useEffect
  // below fires the toast + onClose right after the first mount when
  // the amount is invalid; the SDK init effect is also gated on the
  // same `_amountValid` flag so we never burn an authorisation slot.
  const _amountValid = Number.isFinite(Number(amountCents)) && Number(amountCents) > 0;
  const { currentStore } = useStore();
  const [state, setState] = useState(STATE.IDLE);
  // Approved-but-unsaved: if the parent's onApproved rejects, the card was
  // charged but nothing was recorded. Keep the approval so staff can retry
  // the save from here — never re-charging the card.
  const [internalSaveError, setInternalSaveError] = useState(null);
  const approvedRef = useRef(null);
  const runApproved = (meta, result) => {
    approvedRef.current = { meta, result };
    setInternalSaveError(null);
    // Network blips / server hiccups are retried automatically (same
    // idempotency key on the parent side) so an approved card completes the
    // sale without staff ever seeing a "save" step.
    const attempt = (n) => Promise.resolve()
      .then(() => onApproved?.(meta, result))
      .catch((err) => {
        const status = err?.response?.status;
        const transient = !status || status >= 500;
        if (transient && n < 3) return new Promise((r) => setTimeout(r, 1200 * (n + 1))).then(() => attempt(n + 1));
        throw err;
      });
    return attempt(0)
      .catch((saveErr) => {
        console.error("[tyro] approved but save failed:", saveErr);
        const msg = saveErr?.response?.data?.detail || saveErr?.message || "Save failed";
        setInternalSaveError(typeof msg === "string" ? msg : "Save failed");
        toast.error(`Card APPROVED but not recorded: ${typeof msg === "string" ? msg : "error"}. Do NOT charge again — tap Retry save.`, { duration: 10000 });
      });
  };
  const effectiveSaveError = saveError || internalSaveError;
  const retrySave = () => {
    if (saveError && onRetrySave) return onRetrySave();
    if (approvedRef.current) return runApproved(approvedRef.current.meta, approvedRef.current.result);
    return undefined;
  };
  // Locked display amount. While the SDK is still ramping up (IDLE /
  // RUNNING) we mirror the live `amountCents` prop so the cashier sees
  // any last-second cart edits. Once the terminal returns a terminal
  // result (APPROVED / DECLINED / UNKNOWN) we FREEZE this value so the
  // header + "Approved · charged" line keep showing the amount that
  // was actually transacted — the parent often clears its cart total
  // (POS) or resets `tyroPay.amount` (SaleDetail) during the
  // "Saving sale…" phase, which used to cause the green screen to
  // flip from "$199.00 charged" → "$0.00 charged" right before
  // closing. That UX is gone now.
  const [displayAmount, setDisplayAmount] = useState(amountCents);
  useEffect(() => {
    if (state === STATE.IDLE || state === STATE.RUNNING) {
      setDisplayAmount(amountCents);
    }
  }, [amountCents, state]);
  // When the parent supplies a saved sale after onApproved resolves,
  // smoothly morph from the green "Approved · Saving sale…" beat
  // into the unified SaleCompleteCard action panel embedded in the
  // same window. Refunds keep auto-closing per Tyro cert flow.
  //
  // We DELAY the morph by 800ms so the cashier (and the customer
  // glancing at the cashier's screen) gets a moment to feel the
  // celebration tick + "APPROVED" before the action panel reveals.
  // No point hiding the win behind a sub-100ms flash.
  useEffect(() => {
    if (completedSale && state === STATE.APPROVED && mode === "purchase") {
      const t = setTimeout(() => setState(STATE.SAVED), 800);
      return () => clearTimeout(t);
    }
    if (completedRefund && state === STATE.APPROVED && mode === "refund") {
      const t = setTimeout(() => setState(STATE.SAVED), 800);
      return () => clearTimeout(t);
    }
  }, [completedSale, completedRefund, state, mode]);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [question, setQuestion] = useState(null);   // { text, options, answer }
  // Tyro cert `Retail.Purchase.Signature.1` + `Retail.Refund.Signature.2`:
  // the merchant copy MUST be physically printed BEFORE the cashier
  // confirms "Signature OK?" on the terminal. We surface the print state
  // here so the question UI can DISABLE the affirmative button(s) until
  // the slip has been submitted to the printer (or the cashier explicitly
  // overrides after a print failure).
  //   null      → not a signature prompt (no gating)
  //   "printing"→ POST to /tyro/print-merchant-now in flight
  //   "done"    → backend returned 200; print job is queued at PrintNode
  //   "failed"  → printer offline or error; cashier can manually override
  //   "manual"  → cashier confirmed they printed/wrote by hand
  const [sigPrint, setSigPrint] = useState(null);
  // On-screen copy of the Tyro Merchant Copy receipt — ALWAYS rendered in
  // the signature prompt so the customer can see exactly what they're
  // signing, even when the physical printer is offline / not configured.
  // Also makes the merchant copy visible on Tyro cert screen-recordings.
  const [sigSlipText, setSigSlipText] = useState("");
  // The actual backend error when the auto-print fails (e.g. "No receipt
  // printer configured…") — shown in the failed banner so the cashier
  // knows WHY instead of a generic message.
  const [sigPrintError, setSigPrintError] = useState("");
  // Cert `Retail.Purchase.7.c` — the failure display must mirror the result
  // word from transactionCompleteCallback exactly: DECLINED / CANCELLED /
  // REVERSED each get their own title + colour, not a generic "Declined".
  const [failKind, setFailKind] = useState("DECLINED");
  // Cert `iClient.Retail.Integrated-Receipt-9` — on declined / cancelled /
  // reversed outcomes BOTH the merchant and customer copies must remain
  // available to view/print, clearly showing the result word.
  const [failReceipts, setFailReceipts] = useState(null); // { merchant, customer }
  // Set when the cashier presses Cancel — we do NOT flip the UI ourselves;
  // the result must come from transactionCompleteCallback (Purchase-7.a).
  const cancelRequestedRef = useRef(false);
  // Per-transaction surcharge override. Defaults to the store setting but
  // the cashier can flip it for THIS transaction only without touching
  // Settings → Tyro. Critical during Tyro cert recordings where you have
  // to alternate between surcharge-ON tests (Sur.*, SplitPurchase.*) and
  // surcharge-OFF tests (Signature.*, DCC.*). With surcharge on, $100.08
  // becomes $102.13 and the simulator declines — Signature.1 cert needs
  // EXACTLY .08 cents to reach the simulator to trigger the signature
  // path, so the override lets you turn it off for that one test.
  const [surchargeOverride, setSurchargeOverride] = useState(
    currentStore?.tyro_surcharge_enabled !== false
  );
  // Flips when the cashier presses Cancel — used purely to update the
  // button label/state to "Cancelling…" while we wait for the terminal
  // to return the real CANCELLED result. We never self-declare the
  // outcome (Tyro cert Purchase-7.a).
  const [cancelling, setCancelling] = useState(false);
  const tyroRef = useRef(null);

  const tyroCfg = {
    enabled: !!currentStore?.tyro_enabled,
    env: currentStore?.tyro_env || "test",
    mid: currentStore?.tyro_merchant_id || "",
    tid: currentStore?.tyro_terminal_id || "",
    integrationKey: currentStore?.tyro_integration_key || "",
    // Tyro cert toggles — defaults match cert-recommended values for retail.
    surchargeEnabled: currentStore?.tyro_surcharge_enabled !== false,
    // Controls whether the SDK is asked to return Merchant + Customer
    // copy text on transactionComplete. When ON, the copies are saved
    // against the sale and surface on the invoice page. Independent of
    // the thermal-print toggle below.
    saveEftposCopies: currentStore?.tyro_save_eftpos_copies !== false,
    // Controls whether the SAVED Merchant + Customer copies ALSO get
    // pushed to the thermal printer via PrintNode right after approval.
    // No effect when `saveEftposCopies` is OFF (nothing to print).
    printIntegratedReceipts: currentStore?.tyro_print_integrated_receipts !== false,
  };

  // ─── REF-BACKED TOGGLES (Tyro cert: settings must take effect mid-flight) ──
  // The Tyro SDK callbacks (onQuestion / onReceipt / transactionComplete)
  // fire AFTER start() returns, sometimes seconds later. Capturing
  // the toggles in start()'s closure means a toggle change in
  // /settings/tyro between dialog mount and callback fire is ignored —
  // auto-printing fires for a transaction that should have been
  // non-integrated, and vice versa.
  //
  // The fix is a ref that ALWAYS reflects the latest store value, read
  // at callback execution time.
  const togglesRef = useRef({
    saveEftposCopies:        tyroCfg.saveEftposCopies,
    printIntegratedReceipts: tyroCfg.printIntegratedReceipts,
    surchargeEnabled:        tyroCfg.surchargeEnabled,
  });
  // Sync the ref every time the store config changes. We use a layout
  // effect so the new value is committed BEFORE any SDK callback fires
  // in the same paint cycle — the callbacks themselves are async so
  // they always run after this layout effect resolves.
  useEffect(() => {
    togglesRef.current.saveEftposCopies        = tyroCfg.saveEftposCopies;
    togglesRef.current.printIntegratedReceipts = tyroCfg.printIntegratedReceipts;
    togglesRef.current.surchargeEnabled        = tyroCfg.surchargeEnabled;
  }, [tyroCfg.saveEftposCopies, tyroCfg.printIntegratedReceipts, tyroCfg.surchargeEnabled]);

  const start = async () => {
    setErrorMsg("");
    setQuestion(null);
    setSigSlipText("");
    setSigPrintError("");
    setFailKind("DECLINED");
    setFailReceipts(null);
    cancelRequestedRef.current = false;
    setCancelling(false);
    setState(STATE.RUNNING);
    setStatusMessage("Connecting to terminal…");

    // UI-level fallback — belt and braces. If the SDK Promise chain silently
    // wedges (Chrome storage partitioning intercepting postMessage callbacks
    // from the simulator iframe), the setTimeout inside the SDK wrapper may
    // also never fire. This hook runs completely independently of the SDK
    // and guarantees the dialog shows an actionable error after 100 seconds
    // no matter what. Cleared on success or earlier failure.
    const uiFallback = setTimeout(() => {
      console.warn("[tyro] UI fallback timer fired — SDK Promise never resolved after 100s");
      resetTyroSdk();
      setState(STATE.UNKNOWN);
      setErrorMsg(
        tyroCfg.env === "test"
          ? "No response from Tyro SDK in 100s. This is almost certainly Chrome's storage partitioning blocking the simulator iframe's callbacks. Workarounds: (1) try Firefox or Safari, (2) disable chrome://flags/#third-party-storage-partitioning, or (3) request a preprod physical terminal from Tyro — real terminals aren't affected."
          : "No response from terminal in 100s. Check the terminal screen — the transaction may have completed. Don't retry until you've confirmed."
      );
    }, 100000);   // must outlast the SDK's own 90s timeout, never pre-empt it

    // Receipt blocks captured as the SDK streams them. Declared OUTSIDE the
    // try so the catch block can persist them on failed outcomes too
    // (cert Integrated-Receipt-9: declined/cancelled/reversed receipts must
    // stay available to print).
    let capturedMerchantReceipt = "";
    let capturedCustomerReceipt = "";
    let approvedResult = null;

    try {
      // Fetch the SDK API key just-in-time. In test/preprod the backend
      // returns the public simulator key; in prod it returns the real
      // production key the merchant has saved in Settings → Tyro. The
      // value never lives in the JS bundle so a stolen build can't be
      // replayed against the merchant's live terminal.
      const sdkKeyResp = await api.get(`/stores/${currentStore.id}/tyro/sdk-key`);
      const sdkApiKey = sdkKeyResp?.data?.api_key || "";
      const tyro = await initTyro({
        env: tyroCfg.env,
        apiKey: sdkApiKey,
        // Cert POS.info.1 / POSinfo.2: send POS identification on every
        // request. `safe()` inside initTyro will strip any spaces from
        // the vendor / name / version values so the iClient log passes
        // the no-space rule even if a future build accidentally
        // introduces them.
        posProduct: {
          vendor: "iRepairExperts",
          name: "iRepairPOS",
          version: "1.0.0",
          siteReference: currentStore?.name || currentStore?.id || "default",
        },
      });
      tyroRef.current = tyro;

      // Capture the Tyro receipt blocks as they stream in. With
      // integratedReceipt=true the SDK returns:
      //   • receiptCallback → { merchantReceipt, kind: "merchant" }
      //   • transactionCompleteCallback → { customerReceipt, kind: "customer" }
      // Both blocks need to be persisted on the payment so we can produce
      // the "Tyro Merchant Copy" and "Tyro Customer Copy" PDFs Tyro asks
      // for in their Phase 1 cert evidence (DCC, Surcharge, Signature,
      // Split test cases). Without this we'd have no record of what the
      // terminal actually printed.
      // Once the merchant receipt has been printed once for the signature
      // workflow we don't re-print it on subsequent question callbacks
      // (Tyro sometimes raises a follow-up "Approved OK?" prompt).
      let signatureSlipPrinted = false;

      // Print the Tyro Merchant Copy to the thermal printer NOW so the
      // cashier can capture the wet-ink signature on it BEFORE answering
      // the terminal's "Signature OK?" prompt. Tyro cert requirement.
      // Cert `non-integratedReceipt.Purchase.1` + `non-integratedReceipt.Refund.2`:
      //   When the merchant has turned "Print integrated receipts" OFF
      //   in /settings/tyro, the Tyro TERMINAL prints its own paper
      //   slip — our POS must NOT print any EFTPOS receipt at all
      //   (no signature copy, no merchant copy, no customer copy).
      //   The PDF/ESCPOS receipt code already strips the Tyro slip
      //   block when the flag is false (server.py L5293); here we gate
      //   the inline merchant-copy print that fires during the
      //   signature workflow.
      const printSignatureMerchantSlip = async (text) => {
        if (!text || signatureSlipPrinted) return;
        if (togglesRef.current.printIntegratedReceipts === false) {
          // Thermal print is OFF — terminal already printed its own
          // signature slip. Just surface the text on screen so the
          // cashier has something to compare the customer's signature
          // against, without queueing a paper print.
          signatureSlipPrinted = true;
          setSigSlipText(text);
          setSigPrint("done");
          return;
        }
        signatureSlipPrinted = true;
        // Always surface the slip on screen, independent of print success.
        setSigSlipText(text);
        setSigPrint("printing");
    try {
          await api.post("/tyro/print-merchant-now", {
            receipt_text: text,
            title: "Tyro Merchant Copy · Signature required",
          });
          setSigPrint("done");
        } catch (e) {
          // Non-fatal: don't block the signature confirmation forever.
          // Surface the failure to the UI so the cashier can hand-write
          // the customer's signature on a paper slip and override.
          console.error("[tyro] signature merchant print failed:", e);
          setSigPrintError(e?.response?.data?.detail || e?.message || "Printer error");
          setSigPrint("failed");
          toast.error("Could not auto-print merchant copy — print manually before signing");
        }
      };

      const callbacks = {
        // NOTE: tyroService.purchase/refund expect `onStatus` / `onQuestion`
        // / `onReceipt` — NOT the raw SDK callback names. Passing the raw
        // names (`questionCallback`) silently falls through to the
        // service's default handler which AUTO-ANSWERS the first option
        // ("YES") — that's the bug that made $100.08 straight-approve
        // without ever showing the signature prompt.
        onStatus: (m) => setStatusMessage(m || ""),
        onQuestion: (q) => {
          const text = (q?.text || "").toLowerCase();
          const isSignature = text.includes("signature");
          // CERT NOTE (corrected per Tyro reviewer feedback):
          //   In non-integrated receipt mode the iClient surfaces the
          //   "APPROVED. Print customer copy?" / "Print merchant
          //   copy?" prompt to the POS UI and the CASHIER chooses YES
          //   or NO — the choice instructs the TERMINAL whether to
          //   print on its own paper. We previously auto-answered
          //   silently which broke the cert flow; the prompt is now
          //   shown verbatim like every other Tyro question.
          if (isSignature) {
            // "APPROVED W/ SIGNATURE. Signature OK?" — auto-fire the
            // merchant-copy print so the signed paper is in the
            // cashier's hand when they press Yes. The print state
            // gates the Yes button below.
            if (capturedMerchantReceipt) setSigSlipText(capturedMerchantReceipt);
            printSignatureMerchantSlip(capturedMerchantReceipt);
          } else {
            // Non-signature prompt (Approved OK?, Print customer copy?,
            // Print merchant copy?, surcharge confirm, DCC follow-up,
            // etc.) — no print gating needed.
            setSigPrint(null);
          }
          setQuestion({
            text: q.text || "Question",
            options: (q.options && q.options.length ? q.options : ["YES", "NO"]),
            answer: q.answer,
            isSignature,
          });
        },
        onReceipt: (r) => {
          console.log("[tyro] receipt:", r);
          if (r?.merchantReceipt) capturedMerchantReceipt = r.merchantReceipt;
          if (r?.customerReceipt) capturedCustomerReceipt = r.customerReceipt;
          // Defensive: if the receiptCallback fires AFTER the question
          // (some firmware orders these flipped), still kick the print
          // so the cashier doesn't miss it. Idempotent — guarded by
          // signatureSlipPrinted.
          if (r?.signatureRequired && r?.merchantReceipt) {
            printSignatureMerchantSlip(r.merchantReceipt);
          }
        },
      };

      const fn = mode === "refund" ? tyro.refund : tyro.purchase;
      const params = {
        amountCents: Math.round(amountCents),
        mid: tyroCfg.mid, tid: tyroCfg.tid, integrationKey: tyroCfg.integrationKey,
        // Tyro cert "Tyro Settings Page" toggle — when off, the terminal
        // Capture the Merchant + Customer copy text on
        // transactionComplete iff the merchant has the "Save EFTPOS
        // copies" toggle ON — which is independent from the thermal
        // auto-print toggle. When ON the SDK returns the slip text on
        // both purchase + refund. Read from the ref so a toggle change
        // between dialog mount and start() taking effect still routes
        // to the correct SDK call.
        integratedReceipt: togglesRef.current.saveEftposCopies,
        ...callbacks,
      };
      // Tyro cert "Surcharge" workflow — the POS must flag willingness to
      // accept surcharge on purchases (refunds cannot carry a surcharge).
      // When `true`, Tyro adds the surcharge configured on the merchant's
      // MID on the acquirer side; when the merchant has none configured
      // it's a no-op. Required for Phase 1 cert. In the test simulator,
      // surcharge is only applied for the *50 / *58 magic amounts (e.g.
      // $102.50, $103.50, $104.50, $112.58) — see Tyro docs at
      //   https://integrationsimulator.test.tyro.com/docs/purchase
      if (mode === "purchase") {
        // Per-transaction override — see surchargeOverride state above.
        // Defaults to the store setting but cashier can flip per-tx via
        // the toggle in the dialog header.
        params.enableSurcharge = !!surchargeOverride;
      }
      if (mode === "refund" && originalTxRef) {
        params.transactionId = originalTxRef;
      }

      console.log("[tyro] calling", mode, "with", { amountCents: params.amountCents, mid: params.mid, tid: params.tid, keyLen: (params.integrationKey || "").length });
      const result = await fn(params);
      console.log("[tyro] result received:", result);
      clearTimeout(uiFallback);   // SDK resolved in time — cancel the fallback
      approvedResult = result;
      // Snapshot the amount BEFORE the parent's onApproved callback
      // gets a chance to clear its cart / reset its tyroPay.amount
      // state — otherwise the next render will pass amountCents=0
      // back in via props and the green "Approved · charged" line
      // flickers to "$0.00 charged" right before close.
      setDisplayAmount(amountCents);
      setState(STATE.APPROVED);
      setStatusMessage("");
      // Tyro iClient v1 already returns `baseAmount`, `transactionAmount`
      // and `surchargeAmount` as decimal dollar values (parsed in
      // tyroService). DO NOT divide by 100 — earlier code scaled every
      // receipt by 100x silently. If the SDK omits surchargeAmount but
      // transactionAmount > baseAmount (some firmware versions), fall
      // back to subtraction so we still capture the surcharge.
      const baseDollars = result.baseAmount || 0;
      const txDollars = result.transactionAmount || 0;
      const surchargeFromField = result.surchargeAmount || 0;
      const surchargeDollars = surchargeFromField > 0
        ? surchargeFromField
        : Math.max(0, txDollars - baseDollars);
      // Defensive math: the test simulator has been seen to return mangled
      // values for some magic amounts (e.g. transactionAmount = 37.56 when
      // baseAmount = 114.83 and surchargeAmount = 2.25). Recompute the
      // transaction total from base + surcharge whenever the stored value
      // fails the basic sum check, so the receipt math is internally
      // consistent.
      const sumCheck = +(baseDollars + surchargeDollars).toFixed(2);
      const txClean = Math.abs(txDollars - sumCheck) < 0.01 ? txDollars : sumCheck;
      const meta = {
        transaction_reference: result.transactionReference || "",
        authorisation_code:    result.authorisationCode || "",
        card_type:             result.cardType || "",
        elided_pan:            result.elidedPan || "",
        rrn:                   result.rrn || "",
        base_amount:           baseDollars || (params.amountCents / 100),
        surcharge_amount:      surchargeDollars,
        transaction_amount:    txClean || (params.amountCents / 100),
        mid:                   String(tyroCfg.mid),
        tid:                   String(tyroCfg.tid),
        env:                   tyroCfg.env,
        // Tyro cert (Signature workflow): printed receipts must stamp
        // "Cardholder signed" when the terminal took a paper signature,
        // so the flag needs to survive all the way from SDK → POS → sale
        // → receipt PDF/ESCPOS.
        signature_required:    !!result.signatureRequired,
        // Tyro Phase 1 cert (Dynamic Currency Conversion): when the
        // foreign cardholder elected to pay in their home currency, the
        // SDK exposes structured DCC data which must be persisted on the
        // sale and surfaced on the customer receipt with full disclosure.
        dcc_applied:           !!result.dcc?.applied,
        dcc_currency:          result.dcc?.currency || "",
        dcc_amount:            result.dcc?.amount || 0,
        dcc_exchange_rate:     result.dcc?.exchange_rate || "",
        dcc_markup_pct:        result.dcc?.markup_pct || 0,
        dcc_provider:          result.dcc?.provider || "",
        dcc_customer_receipt:  result.dcc?.customer_receipt || "",
        // Raw Tyro receipt blocks (Phase 1 cert evidence). Both arrive
        // via the SDK's receipt callbacks when integratedReceipt=true.
        // Persisted on payment.tyro_meta + accessible via
        // GET /api/sales/{id}/tyro-receipts for Tyro to download.
        merchant_receipt:      capturedMerchantReceipt || result.merchantReceipt || "",
        customer_receipt:      capturedCustomerReceipt || result.customerReceipt || "",
      };

      // Cert "Integrated receipts ON" path — when the merchant has BOTH
      // "Save EFTPOS copies" AND "Print integrated receipts on thermal"
      // toggles ENABLED in /settings/tyro, the iClient hands us the
      // receipt text via the receipt / transactionComplete callbacks
      // (because we sent `integratedReceipt: true`) AND the POS is
      // expected to produce paper copies for BOTH the merchant and
      // customer through its own printer (PrintNode). We do this on a
      // fire-and-forget basis after raising APPROVED so a printer
      // hiccup never blocks the sale persistence flow downstream.
      //
      // When the THERMAL toggle is OFF the captured copies are still
      // saved to the sale (and downloadable from the invoice page), we
      // just don't auto-push them to paper here — the terminal printed
      // its own paper and we MUST NOT double-print (cert
      // `non-integratedReceipt.Purchase.1` and
      // `non-integratedReceipt.Refund.2`).
      if (togglesRef.current.printIntegratedReceipts !== false
          && togglesRef.current.saveEftposCopies !== false) {
        const isRefundLocal = mode === "refund";
        const merchantTxt = meta.merchant_receipt;
        const customerTxt = meta.customer_receipt;
        const tail = isRefundLocal ? "REFUND" : "PURCHASE";
        // Diagnostics: surface to the console so cert testing can spot
        // why a copy didn't print (no receipt text vs no printer vs
        // PrintNode error). The SDK only returns both copies via the
        // receipt callback when `integratedReceipt: true` was sent at
        // request time — if these are empty even though both toggles
        // are ON the most common cause is a stale paired terminal that
        // needs re-pairing after the toggles changed.
        console.log("[tyro] auto-print integrated receipts", {
          mode,
          saveEftposCopies: togglesRef.current.saveEftposCopies,
          printIntegratedReceipts: togglesRef.current.printIntegratedReceipts,
          haveMerchant: !!merchantTxt,
          haveCustomer: !!customerTxt,
          merchantLen: (merchantTxt || "").length,
          customerLen: (customerTxt || "").length,
        });

        if (!merchantTxt && !customerTxt) {
          toast.error(
            "Tyro returned no receipt text — re-pair the terminal in /settings/tyro after toggling 'Save EFTPOS copies' ON."
          );
        }

        const sendCopy = async (txt, label) => {
          if (!txt) return;
          try {
            await api.post("/tyro/print-merchant-now", {
              receipt_text: txt,
              title: `Tyro ${label} Copy · ${tail}`,
              header: `${label.toUpperCase()} COPY`,
              subheader: `${tail} · APPROVED`,
            });
            toast.success(`Tyro ${label.toLowerCase()} copy sent to printer`);
          } catch (e) {
            const detail = e?.response?.data?.detail || e?.message || "Print failed";
            console.error(`[tyro] auto-print ${label} copy failed:`, e);
            toast.error(`Couldn't print ${label.toLowerCase()} copy: ${detail}`);
          }
        };
        sendCopy(merchantTxt, "Merchant");
        sendCopy(customerTxt, "Customer");
      }

      // The card is APPROVED from here on. A failure while saving must stay
      // an approved-but-unsaved state (with retry) — never "DECLINED".
      runApproved(meta, result);
    } catch (err) {
      clearTimeout(uiFallback);   // SDK rejected — cancel the fallback
      if (approvedResult) {
        console.error("[tyro] post-approval error (card was approved):", err);
        setState(STATE.APPROVED);
        toast.error("Card APPROVED — a display error occurred. Do NOT charge again; check the sale before retrying.", { duration: 10000 });
        return;
      }
      console.error("[tyro] transaction failed:", err);
      resetTyroSdk();
      // Drop our local reference to the dead IClient instance. The next
      // start() rebuilds a fresh one via initTyro(). Without this, a
      // racey retry could try to call .cancel()/.purchase() on the dead
      // client — exactly the symptom the user hit on
      // cancel-then-retry: terminal never lights up.
      tyroRef.current = null;
      const msg = (err?.message || "").toLowerCase();
      const is503Busy = msg.includes("busy") || msg.includes("503") || err?.statusCode === 503;
      if (is503Busy) {
        // The simulator (or a real terminal) is currently holding a
        // pending transaction. Surface a clear, actionable message so the
        // cashier knows to either try again, switch TIDs, or take cash.
        setState(STATE.DECLINED);
        setErrorMsg(
          tyroCfg.env === "test"
            ? `Terminal busy — TID ${tyroCfg.tid} is holding a previous transaction. Settings → Tyro → switch to a different TID, or wait ~5 min.`
            : "Terminal busy — finish the previous transaction on the terminal screen, then retry."
        );
      } else if (err?.timeout) {
        setState(STATE.UNKNOWN);
        setErrorMsg(
          "Tyro didn't respond in time. " +
          (tyroCfg.env === "test"
            ? "If the simulator tab showed APPROVED, browser storage partitioning may be blocking the result from reaching the POS. Use cash for now and re-pair the terminal."
            : "Check the terminal screen — the transaction may have completed. Don't retry until you've confirmed.")
        );
      } else if (err?.unknown) {
        setState(STATE.UNKNOWN);
        setErrorMsg("Transaction status unknown. Check the terminal screen before retrying.");
      } else {
        // Cert Purchase-7.a/7.c: display EXACTLY the result word delivered
        // by transactionCompleteCallback — DECLINED, CANCELLED or REVERSED.
        const word = (err?.result || "").toUpperCase();
        const kind = ["CANCELLED", "REVERSED", "DECLINED"].includes(word) ? word : "DECLINED";
        setFailKind(kind);
        setState(STATE.DECLINED);
        // Cert Retail.Refund.Reversed.4 / Retail.Purchase.Reversed.4: the
        // copy needs to match the workflow — a reversed PURCHASE means no
        // funds were taken, a reversed REFUND means no funds were returned.
        const isRefund = mode === "refund";
        setErrorMsg(
          kind === "CANCELLED"
            ? (isRefund ? "The refund was cancelled on the terminal." : "The transaction was cancelled on the terminal.")
          : kind === "REVERSED"
            ? (isRefund ? "The refund was reversed — no funds were returned to the customer." : "The transaction was reversed — no funds were taken.")
          : (err?.message || (isRefund ? "Refund failed" : "Transaction failed"))
        );
        // Cert Integrated-Receipt-9: keep both copies available, clearly
        // showing the failed result. The customer copy may arrive on the
        // final (rejected) callback; the merchant copy streams earlier.
        const m = capturedMerchantReceipt || err?.merchantReceipt || "";
        const c = capturedCustomerReceipt || err?.customerReceipt || "";
        if (m || c) setFailReceipts({ merchant: m, customer: c });
        if (!err?.message && kind === "DECLINED") toast.error(isRefund ? "Tyro refund failed" : "Tyro transaction failed");
      }
    }
  };

  // Auto-start on mount — guarded so React 18 StrictMode's dev-only
  // double-mount can't fire initiatePurchase() TWICE. Without this the
  // second call hits the terminal while the first is still in flight →
  // "Terminal is busy processing another transaction (503)". Refs survive
  // the simulated unmount/remount, so this runs exactly once per dialog.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    // Defensive amount-zero guard — see comment near `_amountValid`
    // at the top of the component. If the parent opened us with $0 /
    // NaN / negative we toast + close on first mount instead of
    // engaging the SDK.
    if (!_amountValid) {
      toast.error(
        mode === "refund"
          ? "Refund amount must be greater than $0.00."
          : "Sale total must be greater than $0.00 before charging Tyro."
      );
      onClose?.();
      return;
    }
    if (!tyroCfg.enabled) {
      setErrorMsg("Tyro EFTPOS is not enabled for this store.");
      setState(STATE.DECLINED);
      return;
    }
    if (mode === "purchase" && (!tyroCfg.mid || !tyroCfg.tid || !tyroCfg.integrationKey)) {
      setErrorMsg("Terminal isn't paired yet. Pair it under Settings → Integrations → Tyro.");
      setState(STATE.DECLINED);
      return;
    }
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    start();
    // NOTE: no cancel() in cleanup — StrictMode's simulated unmount would
    // cancel the genuine in-flight transaction. Real dismissal goes through
    // the Cancel button / close handlers, which cancel explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;
  const title = mode === "refund" ? "Tyro Refund" : "Tyro EFTPOS";
  const verb  = mode === "refund" ? "refund" : "payment";

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4 bg-[#0B1020]/75 backdrop-blur-md tyro-dialog-root"
      data-testid="tyro-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tyro-dialog-title"
    >
      {/* Scoped animations — keep inside the component so removing the
          dialog removes the styles too. All keyframes are prefixed
          `tyro-` to avoid collision with anything else on the page.

          Only `transform` and `opacity` are animated (GPU-composited).
          `will-change` is set on the targets so the browser promotes
          them to their own layer up-front, eliminating jank on the
          first frame.  `prefers-reduced-motion` disables the infinite
          loops for users who've opted out of motion. */}
      <style>{`
        @keyframes tyro-pop-in {
          0%   { transform: scale(0.92) translateY(12px); opacity: 0; }
          60%  { transform: scale(1.01) translateY(0);   opacity: 1; }
          100% { transform: scale(1)    translateY(0);   opacity: 1; }
        }
        @keyframes tyro-ring {
          0%   { transform: scale(0.55); opacity: 0.85; }
          100% { transform: scale(2.15); opacity: 0;    }
        }
        @keyframes tyro-card-tap {
          0%, 100% { transform: translateY(0)    rotate(-6deg); }
          50%      { transform: translateY(-9px) rotate(-3deg); }
        }
        @keyframes tyro-dot-walk {
          0%   { transform: translateX(-110%); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateX(110%);  opacity: 0; }
        }
        @keyframes tyro-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1;    }
        }
        @keyframes tyro-screen-line {
          0%   { transform: translateY(-100%); opacity: 0; }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(220%);  opacity: 0; }
        }
        @keyframes tyro-check-circle {
          0%   { transform: scale(0.3); opacity: 0; }
          60%  { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
        @keyframes tyro-check-mark {
          0%   { stroke-dashoffset: 60; }
          100% { stroke-dashoffset: 0;  }
        }
        @keyframes tyro-confetti {
          0%   { transform: translate(0,0) scale(0.7);  opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translate(var(--cx, 0), var(--cy, -120px)) scale(1); opacity: 0; }
        }
        @keyframes tyro-x-shake {
          0%,100% { transform: scale(1)    translateX(0); }
          15%     { transform: scale(1.05) translateX(-6px); }
          30%     { transform: scale(1)    translateX(6px);  }
          45%     { transform: scale(1)    translateX(-4px); }
          60%     { transform: scale(1)    translateX(4px);  }
          80%     { transform: scale(1)    translateX(0);    }
        }
        @keyframes tyro-body-fade {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0);   }
        }
        @keyframes tyro-halo {
          0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.6); }
          40%  { opacity: 1; transform: translate(-50%, -50%) scale(1.05); }
          100% { opacity: 0.85; transform: translate(-50%, -50%) scale(1); }
        }
        .tyro-halo-anim {
          animation: tyro-halo 700ms cubic-bezier(.2,.85,.3,1) both;
          will-change: transform, opacity;
        }
        @keyframes tyro-pulse-ring {
          0%   { opacity: 0.85; transform: translate(-50%, -50%) scale(0.7); }
          80%  { opacity: 0;    transform: translate(-50%, -50%) scale(2.0); }
          100% { opacity: 0;    transform: translate(-50%, -50%) scale(2.0); }
        }
        .tyro-pulse-ring {
          animation: tyro-pulse-ring 1400ms cubic-bezier(.2,.85,.3,1) both;
          animation-iteration-count: 1;
          will-change: transform, opacity;
        }
        @keyframes tyro-approved-fade-out {
          0%, 70% { opacity: 1; transform: translateY(0) scale(1); }
          100%    { opacity: 0; transform: translateY(-6px) scale(0.96); }
        }
        /* Only kicks in via JS by re-keying — see the body wrappers
           key prop which remounts the element when state changes. */
        @keyframes tyro-header-sheen {
          0%   { transform: translateX(-120%) skewX(-22deg); opacity: 0; }
          18%  { opacity: 0.9; }
          100% { transform: translateX(220%) skewX(-22deg); opacity: 0; }
        }
        .tyro-header-sheen {
          position: absolute;
          top: 0;
          left: 0;
          width: 38%;
          height: 100%;
          pointer-events: none;
          background: linear-gradient(
            100deg,
            rgba(255,255,255,0)   0%,
            rgba(255,255,255,0.18) 45%,
            rgba(255,255,255,0.38) 50%,
            rgba(255,255,255,0.18) 55%,
            rgba(255,255,255,0)   100%
          );
          mix-blend-mode: screen;
          animation: tyro-header-sheen 1200ms cubic-bezier(.22,1,.36,1) 80ms both;
          will-change: transform, opacity;
        }
        @keyframes sale-complete-celebration-bar {
          0%   { opacity: 0; transform: scale(0.96) translateY(-4px); }
          60%  { opacity: 1; transform: scale(1.015) translateY(0);   }
          100% { opacity: 1; transform: scale(1) translateY(0);       }
        }
        .sale-complete-celebration-bar {
          animation: sale-complete-celebration-bar .42s cubic-bezier(.2,.85,.3,1.05) both;
          transform-origin: top center;
          will-change: transform, opacity;
        }
        .tyro-card-anim       { animation: tyro-pop-in .42s cubic-bezier(.2,.85,.3,1.05) both; will-change: transform, opacity; }
        .tyro-ring-anim       { animation: tyro-ring 1.8s ease-out infinite; will-change: transform, opacity; }
        .tyro-card-tap-anim   { animation: tyro-card-tap 1.6s ease-in-out infinite; will-change: transform; }
        .tyro-dot-walk-anim   { animation: tyro-dot-walk 1.6s linear infinite; will-change: transform, opacity; }
        .tyro-pulse-anim      { animation: tyro-pulse 1.3s ease-in-out infinite; }
        .tyro-screen-line-anim{ animation: tyro-screen-line 2.2s ease-in-out infinite; will-change: transform, opacity; }
        .tyro-check-c-anim    { animation: tyro-check-circle .42s cubic-bezier(.2,.85,.3,1.05) both; will-change: transform, opacity; }
        .tyro-check-m-anim    { stroke-dasharray: 60; stroke-dashoffset: 60; animation: tyro-check-mark .45s ease-out .25s forwards; }
        .tyro-confetti-anim   { animation: tyro-confetti 1.1s cubic-bezier(.2,.7,.4,1) forwards; will-change: transform, opacity; }
        .tyro-x-shake-anim    { animation: tyro-x-shake .55s ease-in-out both; will-change: transform; }
        .tyro-body-fade-anim  { animation: tyro-body-fade .28s ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .tyro-ring-anim, .tyro-card-tap-anim, .tyro-dot-walk-anim,
          .tyro-pulse-anim, .tyro-screen-line-anim, .tyro-confetti-anim,
          .tyro-x-shake-anim {
            animation: none !important;
          }
          .tyro-card-anim, .tyro-check-c-anim, .tyro-body-fade-anim {
            animation-duration: .12s !important;
          }
        }
      `}</style>
      <div
        className="tyro-card-anim w-full max-w-[440px] bg-white rounded-[24px] overflow-hidden relative"
        style={{ boxShadow: "0 32px 80px rgba(8, 18, 48, 0.45), 0 0 0 1px rgba(255,255,255,0.04)" }}
      >
        {/* ── HEADER ─────────────────────────────────────────────
            Deep-blue brand band with subtle grain. The live status
            dot pulses while the SDK is talking to the terminal. */}
        <div
          className="relative px-5 pt-5 pb-4 text-white overflow-hidden"
          style={{
            background: state === STATE.APPROVED
              ? "linear-gradient(135deg, #047857 0%, #059669 60%, #064E3B 100%)"
              : state === STATE.SAVED
                ? (mode === "refund"
                    ? "linear-gradient(135deg, #DC2626 0%, #991B1B 50%, #450A0A 100%)"
                    : "linear-gradient(135deg, #1E5FEB 0%, #1849C8 50%, #0F1F4E 100%)")
              : state === STATE.DECLINED
                ? (failKind === "CANCELLED"
                    ? "linear-gradient(135deg, #B45309 0%, #92400E 60%, #451A03 100%)"
                    : failKind === "REVERSED"
                      ? "linear-gradient(135deg, #6D28D9 0%, #5B21B6 60%, #2E1065 100%)"
                      : "linear-gradient(135deg, #B91C1C 0%, #991B1B 60%, #450A0A 100%)")
                : state === STATE.UNKNOWN
                  ? "linear-gradient(135deg, #B45309 0%, #92400E 60%, #451A03 100%)"
                  : "linear-gradient(135deg, #1E5FEB 0%, #1849C8 50%, #0F1F4E 100%)",
            transition: "background 350ms ease",
          }}
        >
          {/* Subtle grain layer for premium depth */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-overlay"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.9) 1px, transparent 0)",
              backgroundSize: "3px 3px",
            }}
          />
          {/* One-shot diagonal sheen — fires the moment the dialog
              morphs from APPROVED (green) into SAVED (blue), so the
              colour change reads as a single confident transition
              rather than a swap. `key={state}` re-mounts on state
              change so the animation re-runs cleanly. */}
          {state === STATE.SAVED && (
            <div key="saved-sheen" aria-hidden className="tyro-header-sheen" />
          )}
          <div className="relative flex items-start gap-3">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
              style={{
                background: "rgba(255,255,255,0.16)",
                backdropFilter: "blur(8px)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
              }}
            >
              {/* Tyro brand mark on EVERY purchase state (IDLE while
                  the SDK boots, RUNNING during the transaction, APPROVED,
                  SAVED) — the cashier should see the EFTPOS provider
                  on the whole journey, not just at the end. Sized
                  generously so it reads at a glance. Falls back to
                  CreditCard for refund / declined / unknown screens
                  where the brand stamp would be misleading. */}
              {mode === "purchase" && (state === STATE.IDLE || state === STATE.RUNNING || state === STATE.APPROVED || state === STATE.SAVED) ? (
                <img
                  src="/tyro-logo.gif"
                  alt="Tyro"
                  className="h-5 w-auto drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
                  style={{ filter: "brightness(0) invert(1)" }}
                  data-testid="tyro-header-logo"
                />
              ) : (
                <CreditCard size={20} weight="fill" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div id="tyro-dialog-title" className="font-display font-semibold text-[17px] leading-tight tracking-tight">{title}</div>
              <div className="text-[10.5px] mt-1 font-mono uppercase tracking-[0.14em] opacity-75 flex items-center gap-2">
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${state === STATE.RUNNING ? "tyro-pulse-anim" : ""}`}
                  style={{
                    background: state === STATE.APPROVED ? "#34D399"
                      : state === STATE.SAVED ? "#7DD3FC"
                      : state === STATE.DECLINED ? "#FCA5A5"
                      : state === STATE.UNKNOWN ? "#FCD34D"
                      : "#7DD3FC",
                    boxShadow: "0 0 0 3px rgba(255,255,255,0.12)",
                  }}
                />
                <span className="truncate">{tyroCfg.env} · MID {tyroCfg.mid || "—"} · TID {tyroCfg.tid || "—"}</span>
              </div>
            </div>
            {/* Amount badge in the header — hidden in SAVED morph
                because the green "APPROVED · $X.XX charged" pill
                already shows it directly below, no need to repeat. */}
            {state !== STATE.SAVED && (
              <div className="text-right shrink-0">
                <div className="text-[9.5px] uppercase opacity-70 tracking-[0.18em] font-semibold">Amount</div>
                <div className="font-display font-bold text-[24px] tabular-nums leading-none mt-1">{dollars(displayAmount)}</div>
              </div>
            )}
            {state !== STATE.RUNNING && state !== STATE.SAVED && (
              <button
                onClick={() => {
                  try { tyroRef.current?.cancel?.(); } catch { /* noop */ }
                  resetTyroSdk();
                  onClose?.();
                }}
                className="absolute -top-1 -right-1 w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors"
                data-testid="tyro-force-close-btn"
                title="Close"
                aria-label="Close"
              >
                <X size={16} weight="bold" />
              </button>
            )}
          </div>
        </div>

        {/* Per-transaction surcharge toggle — only meaningful before the
            transaction starts. Hidden during/after the SDK call.
            Critical for Tyro cert recordings: different test cases need
            opposite surcharge states (Signature.1 needs OFF for the
            $100.08 magic; Sur.1 needs ON for the $102.50 magic). */}
        {mode === "purchase" && state === STATE.IDLE && (
          <div className="px-6 py-3 border-b border-[#F3F4F6] bg-[#FBFBFD] flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[11.5px] font-semibold text-[#0F172A] uppercase tracking-wide">
                Apply Tyro surcharge
              </div>
              <div className="text-[10.5px] text-[#6B7280] mt-0.5">
                {surchargeOverride
                  ? "Tyro will add the configured surcharge to this transaction."
                  : "No surcharge will be added. Required for cert Signature & DCC tests."}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSurchargeOverride((v) => !v)}
              data-testid="tyro-surcharge-toggle"
              role="switch"
              aria-checked={surchargeOverride}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1E5FEB] ${
                surchargeOverride ? "bg-[#1E5FEB]" : "bg-[#D1D5DB]"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  surchargeOverride ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
        )}

        {/* ── BODY ─────────────────────────────────────────────────
            Keyed by state so React tears down the previous state's
            DOM (and any pending animations on it) and mounts the new
            state cleanly — eliminates any chance of stale animation
            frames bleeding between states (no jank on retry). */}
        <div
          key={state}
          className={`tyro-body-fade-anim ${
            state === STATE.SAVED
              ? "px-0 pt-0 pb-0"
              : "px-6 pt-7 pb-6 min-h-[260px] flex flex-col items-center justify-center text-center"
          }`}
        >
          {state === STATE.RUNNING && (
            <>
              {/* Stylized EFTPOS terminal with a card 'tapping' it and
                  pulse rings emanating outward. Replaces the generic
                  spinner so staff get a clear visual cue that we're
                  talking to a physical device. Uses pure CSS — no JS
                  ticker, no heavy SVG, and animates smoothly even on
                  the cheapest till hardware. */}
              <div className="relative w-[150px] h-[110px] mb-5 flex items-center justify-center">
                {/* pulse rings */}
                <span
                  className="absolute w-[80px] h-[80px] rounded-full tyro-ring-anim"
                  style={{ border: "2px solid #1E5FEB", animationDelay: "0s" }}
                />
                <span
                  className="absolute w-[80px] h-[80px] rounded-full tyro-ring-anim"
                  style={{ border: "2px solid #1E5FEB", animationDelay: ".6s" }}
                />
                <span
                  className="absolute w-[80px] h-[80px] rounded-full tyro-ring-anim"
                  style={{ border: "2px solid #1E5FEB", animationDelay: "1.2s" }}
                />
                {/* terminal body */}
                <svg viewBox="0 0 80 100" width="74" height="92" className="relative drop-shadow-md">
                  <defs>
                    <linearGradient id="tyroTerminalBody" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#1F2937" />
                      <stop offset="1" stopColor="#0F172A" />
                    </linearGradient>
                    <linearGradient id="tyroTerminalScreen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0" stopColor="#1E5FEB" />
                      <stop offset="1" stopColor="#1849C8" />
                    </linearGradient>
                  </defs>
                  <rect x="6" y="4" width="68" height="92" rx="9" fill="url(#tyroTerminalBody)" />
                  <rect x="12" y="11" width="56" height="34" rx="3" fill="url(#tyroTerminalScreen)" />
                  {/* live screen text */}
                  <rect x="16" y="17" width="34" height="3" rx="1.5" fill="#fff" opacity="0.7" />
                  <rect x="16" y="23" width="22" height="3" rx="1.5" fill="#fff" opacity="0.5" />
                  <rect x="16" y="35" width="40" height="4" rx="2" fill="#fff" opacity="0.9" />
                  {/* scanning line — gives the terminal a "thinking" feel */}
                  <g clipPath="url(#tyroScreenClip)">
                    <rect x="12" y="11" width="56" height="3" fill="#fff" opacity="0.35" className="tyro-screen-line-anim" />
                  </g>
                  <defs>
                    <clipPath id="tyroScreenClip">
                      <rect x="12" y="11" width="56" height="34" rx="3" />
                    </clipPath>
                  </defs>
                  {/* keypad */}
                  {[0,1,2].map((c) => [0,1,2,3].map((r) => (
                    <circle key={`k-${c}-${r}`} cx={20 + c*16} cy={56 + r*9} r="3.2" fill="#374151" />
                  )))}
                </svg>
                {/* card hovering over terminal */}
                <div className="absolute -top-1 right-1 tyro-card-tap-anim">
                  <svg viewBox="0 0 46 30" width="46" height="30" className="drop-shadow-md">
                    <defs>
                      <linearGradient id="tyroCard" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="#60A5FA" />
                        <stop offset="1" stopColor="#1E40AF" />
                      </linearGradient>
                    </defs>
                    <rect x="0.5" y="0.5" width="45" height="29" rx="4" fill="url(#tyroCard)" stroke="rgba(255,255,255,0.4)" />
                    <rect x="5" y="9" width="9" height="6" rx="1" fill="#FCD34D" />
                    <circle cx="38" cy="14" r="4" fill="#fff" opacity="0.6" />
                    <circle cx="34" cy="14" r="4" fill="#fff" opacity="0.4" />
                    <rect x="5" y="20" width="16" height="2" rx="1" fill="#fff" opacity="0.55" />
                  </svg>
                </div>
              </div>

              <div className="font-display font-semibold text-[17px] text-[#0F172A]">Follow the terminal</div>
              <div className="text-[13px] text-[#4B5563] mt-1.5 min-h-[1.4rem] max-w-[280px]" data-testid="tyro-status">
                {statusMessage || `Initiating ${verb}…`}
              </div>

              {/* moving dot bar — Apple Pay style "we're working on it" */}
              <div className="mt-5 relative w-[180px] h-[3px] rounded-full bg-[#E5E7EB] overflow-hidden">
                <span
                  className="absolute top-0 left-0 w-1/3 h-full rounded-full tyro-dot-walk-anim"
                  style={{ background: "linear-gradient(90deg, transparent 0%, #1E5FEB 50%, transparent 100%)" }}
                />
              </div>

              <button
                onClick={() => {
                  // Cert Headless-2b: Cancel calls cancelCurrentTransaction()
                  // and the UI then shows the result delivered by
                  // transactionCompleteCallback (CANCELLED) — we never
                  // self-declare the outcome (Purchase-7.a).
                  if (cancelling) return;
                  setCancelling(true);
                  cancelRequestedRef.current = true;
                  setStatusMessage("Cancelling — waiting for terminal…");
                  try { tyroRef.current?.cancel?.(); } catch { /* noop */ }
                }}
                disabled={cancelling}
                className={`mt-7 group h-11 px-6 rounded-full text-[11.5px] uppercase tracking-[0.18em] font-bold transition-all duration-200 flex items-center gap-2 ${
                  cancelling
                    ? "bg-[#FEF2F2] border-2 border-[#FCA5A5] text-[#DC2626] cursor-not-allowed"
                    : "bg-white border-2 border-[#FCA5A5] text-[#DC2626] hover:bg-[#DC2626] hover:text-white hover:border-[#DC2626] active:scale-[0.97]"
                }`}
                data-testid="tyro-cancel-btn"
                aria-label={cancelling ? "Cancelling transaction" : "Cancel transaction"}
              >
                {cancelling ? (
                  <>
                    <Spinner size={13} weight="bold" className="animate-spin" />
                    Cancelling…
                  </>
                ) : (
                  <>
                    <X size={13} weight="bold" />
                    Cancel transaction
                  </>
                )}
              </button>
            </>
          )}

          {state === STATE.APPROVED && (
            <>
              {/* Premium "approved" celebration — confetti burst + two
                  expanding ring waves + radial halo behind the check.
                  All CSS, no libs. The whole composition is keyed off
                  `state === APPROVED` so it re-plays on every win. */}
              <div className="relative mb-4 tyro-approved-fade-out">
                {/* Radial halo BEHIND everything for a soft glow that
                    bleeds into the white body. */}
                <div
                  aria-hidden
                  className="absolute inset-0 tyro-halo-anim pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(16,185,129,0.32) 0%, rgba(16,185,129,0.18) 35%, rgba(16,185,129,0) 70%)",
                    width: "260px",
                    height: "260px",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    borderRadius: "50%",
                  }}
                />
                {/* Two concentric pulse rings */}
                {[0, 1].map((i) => (
                  <span
                    key={`ring-${i}`}
                    aria-hidden
                    className="absolute tyro-pulse-ring"
                    style={{
                      left: "50%",
                      top: "50%",
                      width: "88px",
                      height: "88px",
                      transform: "translate(-50%, -50%)",
                      borderRadius: "50%",
                      border: "2px solid rgba(16,185,129,0.6)",
                      animationDelay: `${i * 0.4}s`,
                    }}
                  />
                ))}
                {/* Confetti — 12 particles fanning out */}
                {[
                  ["#0F9D58", -90, -90],  ["#34D399", -55, -125], ["#1E5FEB", -15, -140],
                  ["#22D3EE",  25, -135], ["#FCD34D",  65, -110], ["#F472B6", 100, -65],
                  ["#FB7185",  85,   5],  ["#34D399",  35,  35],  ["#10B981", -10,  35],
                  ["#1E5FEB", -55,  20],  ["#FCD34D", -90, -20],  ["#22D3EE", -70, -60],
                ].map(([color, cx, cy], i) => (
                  <span
                    key={i}
                    className="absolute tyro-confetti-anim rounded-full"
                    style={{
                      left: "50%", top: "50%",
                      width: i % 2 ? "8px" : "6px",
                      height: i % 2 ? "8px" : "6px",
                      background: color,
                      "--cx": `${cx}px`, "--cy": `${cy}px`,
                      animationDelay: `${i * 0.022}s`,
                    }}
                  />
                ))}
                {/* Checkmark disc with soft inner highlight */}
                <div
                  className="relative w-[96px] h-[96px] rounded-full flex items-center justify-center tyro-check-c-anim"
                  style={{
                    background: "linear-gradient(135deg, #10B981 0%, #047857 100%)",
                    boxShadow:
                      "0 18px 44px rgba(16,185,129,0.42), 0 0 0 6px rgba(16,185,129,0.10), inset 0 1px 0 rgba(255,255,255,0.30)",
                  }}
                >
                  {/* Soft top highlight for a premium glass dome feel */}
                  <span
                    aria-hidden
                    className="absolute inset-x-0 top-0 h-1/2 rounded-t-full pointer-events-none"
                    style={{
                      background: "linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0) 100%)",
                    }}
                  />
                  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" className="relative">
                    <path
                      d="M5 12.5 L10 17.5 L19 7.5"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="tyro-check-m-anim"
                    />
                  </svg>
                </div>
              </div>
              <div className="font-display font-semibold text-[24px] text-[#0F172A] tracking-tight tyro-approved-fade-out">Approved</div>
              <div className="text-[13px] text-[#6B7280] mt-1 font-mono tabular-nums tyro-approved-fade-out">
                {dollars(displayAmount)} · charged
              </div>
              {effectiveSaveError ? (
                <div className="mt-4 w-full max-w-sm mx-auto" data-testid="tyro-save-error">
                  <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-left">
                    <div className="text-[12px] font-bold text-amber-800">Card charged — but saving the sale failed</div>
                    <div className="text-[11px] text-amber-700 mt-1">{effectiveSaveError}</div>
                    <div className="text-[11px] text-amber-600 mt-1">Retrying won't charge the card again.</div>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => retrySave()}
                      data-testid="tyro-retry-save"
                      className="flex-1 h-10 rounded-lg bg-[#10B981] hover:bg-[#059669] text-white text-[12px] font-bold"
                    >
                      Retry saving
                    </button>
                    <button
                      onClick={() => onClose?.()}
                      data-testid="tyro-save-error-close"
                      className="h-10 px-4 rounded-lg border border-zinc-300 text-zinc-600 text-[12px] font-semibold hover:bg-zinc-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-[#10B981] font-semibold uppercase tracking-[0.14em] tyro-approved-fade-out">
                  <Spinner size={12} weight="bold" className="animate-spin" />
                  Saving sale…
                </div>
              )}
            </>
          )}

          {state === STATE.SAVED && (completedSale || completedRefund) && (
            <div className="w-full" data-testid="tyro-saved-morph">
              {/* Action panel — staggered fade-in handled by the
                  SaleCompleteCard's own internal animations. */}
              <SaleCompleteCard
                open
                mode="inline"
                kind={mode === "refund" ? "refund" : "sale"}
                sale={completedSale || completedRefund}
                customer={customer}
                repairTicket={repairTicket}
                store={storeForActions || currentStore}
                onPrintReceipt={onPrintReceipt}
                onPrintLabel={onPrintLabel}
                onViewInvoice={onViewInvoice}
                onClose={onClose}
              />
            </div>
          )}

          {state === STATE.DECLINED && (
            <>
              <div className="relative mb-4 tyro-x-shake-anim">
                <div
                  className="w-[88px] h-[88px] rounded-full flex items-center justify-center"
                  style={{
                    background: failKind === "CANCELLED"
                      ? "linear-gradient(135deg, #F59E0B 0%, #B45309 100%)"
                      : failKind === "REVERSED"
                        ? "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
                        : "linear-gradient(135deg, #EF4444 0%, #B91C1C 100%)",
                    boxShadow: failKind === "CANCELLED"
                      ? "0 14px 38px rgba(245, 158, 11, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)"
                      : failKind === "REVERSED"
                        ? "0 14px 38px rgba(139, 92, 246, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)"
                        : "0 14px 38px rgba(239, 68, 68, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                  }}
                >
                  {failKind === "CANCELLED" ? (
                    <WarningCircle size={44} weight="fill" className="text-white" />
                  ) : failKind === "REVERSED" ? (
                    <ArrowCounterClockwise size={42} weight="bold" className="text-white" />
                  ) : (
                    <XCircle size={44} weight="fill" className="text-white" />
                  )}
                </div>
              </div>
              <div
                className={`font-display font-semibold text-[22px] tracking-tight ${
                  failKind === "CANCELLED" ? "text-[#92400E]"
                  : failKind === "REVERSED" ? "text-[#6D28D9]"
                  : "text-[#B91C1C]"
                }`}
                data-testid="tyro-result-word"
              >
                {failKind === "CANCELLED" ? "Cancelled" : failKind === "REVERSED" ? "Reversed" : "Declined"}
              </div>
              <div className="text-[13px] text-[#4B5563] mt-2 max-w-[300px] leading-snug" data-testid="tyro-error">{errorMsg}</div>

              {/* Cert iClient.Retail.Integrated-Receipt-9: on DECLINED /
                  CANCELLED / REVERSED both copies remain available, each
                  showing the result word printed CLEARLY ON THE RECEIPT
                  BODY (not just in the UI chrome around it).  The Tyro
                  simulator doesn't always inject the result word into the
                  customer / merchant receipt text it hands back to the
                  POS — so we stamp a top banner into the slip ourselves
                  if the iClient text doesn't already include the word.
                  The same stamped text is sent to PrintNode so the
                  physical paper also shows REVERSED / DECLINED /
                  CANCELLED at the top of the slip. */}
              {failReceipts && (failReceipts.merchant || failReceipts.customer) && (
                <div className="w-full mt-4 space-y-2 text-left" data-testid="tyro-fail-receipts">
                  {[["merchant", "Merchant Copy"], ["customer", "Customer Copy"]].map(([k, label]) => {
                    const raw = failReceipts[k];
                    if (!raw) return null;
                    const word = failKind; // DECLINED | CANCELLED | REVERSED
                    // Only inject the banner once and only if the iClient
                    // text doesn't already include the result word.
                    const alreadyStamped = new RegExp(`\\b${word}\\b`, "i").test(raw);
                    const label2 = `${mode === "refund" ? "REFUND" : "TRANSACTION"} ${word}`;
                    const width = 32;
                    const pad = Math.max(0, Math.floor((width - label2.length - 2) / 2));
                    const padR = Math.max(0, width - label2.length - 2 - pad);
                    const bar = "*".repeat(width);
                    const centered = `*${" ".repeat(pad)}${label2}${" ".repeat(padR)}*`;
                    const stamped = alreadyStamped
                      ? raw
                      : `${bar}\n${centered}\n${bar}\n\n${raw}`;
                    return (
                      <details key={k} className="rounded-lg border border-[#E5E7EB] bg-[#FAFAFA]" open>
                        <summary
                          className="cursor-pointer select-none px-3 py-2 text-[12px] font-semibold text-[#374151] flex items-center justify-between"
                          data-testid={`tyro-fail-receipt-${k}`}
                        >
                          {label} · {failKind}
                        </summary>
                        <div className="px-3 pb-3">
                          <pre className="font-mono text-[10px] leading-[1.35] whitespace-pre-wrap bg-white border border-[#E5E7EB] rounded-md p-2 max-h-[220px] overflow-y-auto" data-testid={`tyro-fail-receipt-body-${k}`}>{stamped}</pre>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await api.post("/tyro/print-merchant-now", {
                                  receipt_text: stamped,
                                  title: `Tyro ${label} · ${failKind}`,
                                  header: label.toUpperCase(),
                                  subheader: `${mode === "refund" ? "Refund" : "Transaction"} ${failKind}`,
                                });
                                toast.success(`${label} sent to printer`);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Print failed");
                              }
                            }}
                            className="mt-2 h-8 px-3 rounded-md bg-[#111827] text-white text-[11px] font-bold hover:bg-[#374151]"
                            data-testid={`tyro-fail-receipt-print-${k}`}
                          >
                            PRINT {label.toUpperCase()}
                          </button>
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {state === STATE.UNKNOWN && (
            <>
              <div
                className="w-[80px] h-[80px] rounded-full mb-3 flex items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, #F59E0B 0%, #B45309 100%)",
                  boxShadow: "0 14px 38px rgba(245, 158, 11, 0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
                }}
              >
                <WarningCircle size={40} weight="fill" className="text-white" />
              </div>
              <div className="font-display font-semibold text-[20px] text-[#92400E] tracking-tight">Status unknown</div>
              <div className="text-[13px] text-[#4B5563] mt-2 max-w-[300px] leading-snug">{errorMsg}</div>
            </>
          )}
        </div>

        {/* Question prompt overlay */}
        {question && (
          <div className="px-6 pb-5">
            <div className="rounded-xl border-2 border-[#1E5FEB] bg-[#EFF6FF] p-4">
              <div className="font-semibold text-[14px] mb-2 text-[#1849C8]" data-testid="tyro-question">{question.text}</div>

              {/* On-screen Merchant Copy — the slip the customer signs.
                  Rendered as an 80mm-style thermal preview so the customer
                  can read it even if the physical printer is offline, and
                  so the copy is visible on Tyro cert screen-recordings. */}
              {question.isSignature && sigSlipText && (
                <div
                  className="mb-3 mx-auto w-[290px] max-h-[260px] overflow-y-auto rounded-md bg-white border border-[#D1D5DB] shadow-sm px-3 py-2"
                  data-testid="tyro-sig-slip-preview"
                >
                  <pre className="font-mono text-[10.5px] leading-[1.35] text-[#111827] whitespace-pre-wrap">{sigSlipText}</pre>
                  <div className="mt-1 border-t border-dashed border-[#9CA3AF] pt-3 pb-1">
                    <div className="h-8 border-b border-[#111827]" />
                    <div className="text-[10px] text-[#6B7280] mt-1 font-mono">CUSTOMER SIGNATURE — retain on file</div>
                  </div>
                </div>
              )}

              {/* Tyro cert `Retail.Purchase.Signature.1`: the affirmative
                  answer ("Yes") MUST NOT be clickable until the merchant
                  copy has been printed. This banner + button gating
                  proves the ordering on camera. */}
              {question.isSignature && sigPrint === "printing" && (
                <div
                  className="flex items-center gap-2 mb-3 text-[12px] text-[#1849C8] font-semibold"
                  data-testid="tyro-sig-print-status"
                >
                  <Spinner size={14} weight="bold" className="animate-spin" />
                  Printing Merchant Copy… wait for the slip to eject before pressing Yes.
                </div>
              )}
              {question.isSignature && sigPrint === "done" && (
                <div
                  className="flex items-center gap-2 mb-3 text-[12px] text-[#0F9D58] font-semibold"
                  data-testid="tyro-sig-print-status"
                >
                  <CheckCircle size={14} weight="bold" />
                  Merchant Copy sent to printer — hand it to the customer for signature.
                </div>
              )}
              {question.isSignature && sigPrint === "failed" && (
                <div
                  className="flex flex-col gap-2 mb-3 p-2 rounded-md bg-[#FEF2F2] border border-[#FCA5A5] text-[12px] text-[#991B1B]"
                  data-testid="tyro-sig-print-status"
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <WarningCircle size={14} weight="bold" />
                    Auto-print failed{sigPrintError ? `: ${sigPrintError}` : "."}
                  </div>
                  <div>
                    Have the customer sign the on-screen copy above (or a hand-written slip), then confirm:
                  </div>
                  <button
                    type="button"
                    onClick={() => setSigPrint("manual")}
                    className="self-start h-7 px-2.5 rounded-md bg-[#991B1B] text-white text-[11px] font-bold hover:bg-[#7F1D1D]"
                    data-testid="tyro-sig-print-manual-override"
                  >
                    SIGNATURE CAPTURED — CONTINUE
                  </button>
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                {(question.options || []).map((opt) => {
                  const norm = (opt || "").toString().toLowerCase().trim();
                  const isAffirmative = norm === "yes" || norm.startsWith("ok") || norm === "y";
                  // Gate the AFFIRMATIVE option(s) on signature prompts
                  // until the merchant slip has confirmed. NO / cancel
                  // options are always clickable so the cashier can
                  // reject a forged/missing signature instantly.
                  const blocked =
                    question.isSignature &&
                    isAffirmative &&
                    !(sigPrint === "done" || sigPrint === "manual");
                  return (
                    <button
                      key={opt}
                      onClick={() => {
                        if (blocked) return;
                        // CERT FIX (Retail.Refund.DCC.2 + Retail.Refund.DCC.1 +
                        // any multi-question flow such as healthpoint /
                        // signature follow-ups):
                        //
                        //   The iClient simulator can fire the NEXT
                        //   questionCallback SYNCHRONOUSLY from inside
                        //   answerCallback() — i.e. before this click
                        //   handler returns. React 18 then batches the
                        //   inner setQuestion(newQuestion) and our outer
                        //   setQuestion(null) in the same tick — and the
                        //   `null` (run last) WINS. That swallows every
                        //   prompt after Q1 ("Proceed with refund as EUR
                        //   …?" → YES → Q2 "Was original tx in EUR?"
                        //   never appears) and silently breaks the DCC
                        //   refund cert workflow.
                        //
                        //   Correct order: clear the modal state FIRST,
                        //   then defer answerCallback to the next
                        //   macrotask so any subsequent questionCallback
                        //   the SDK fires lands in a fresh React batch
                        //   that ONLY contains setQuestion(newQuestion) —
                        //   so the new prompt actually renders.
                        const answer = question.answer;
                        const chosen = opt;
                        setQuestion(null);
                        setSigPrint(null);
                        setTimeout(() => {
                          try { answer(chosen); } catch (e) { console.error("[tyro] answerCallback threw:", e); }
                        }, 0);
                      }}
                      disabled={blocked}
                      className={
                        blocked
                          ? "h-10 px-4 rounded-lg bg-[#F3F4F6] border border-[#E5E7EB] text-[13px] font-semibold text-[#9CA3AF] cursor-not-allowed"
                          : "h-10 px-4 rounded-lg bg-white border border-[#1E5FEB]/30 text-[13px] font-semibold text-[#1849C8] hover:bg-[#1E5FEB] hover:text-white transition-colors"
                      }
                      data-testid={`tyro-answer-${opt.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      title={blocked ? "Waiting for merchant copy to print" : undefined}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F3F4F6] bg-[#FAFBFC] flex items-center justify-end gap-2">
          {(state === STATE.DECLINED || state === STATE.UNKNOWN) && (
            <>
              <button
                onClick={onClose}
                className="h-11 px-5 rounded-full bg-white border border-[#E5E7EB] text-[12px] font-bold uppercase tracking-[0.12em] text-[#4B5563] hover:bg-[#F3F4F6] transition-colors"
                data-testid="tyro-close-btn"
              >
                Close
              </button>
              <button
                onClick={() => { setState(STATE.IDLE); start(); }}
                className="h-11 px-5 rounded-full text-white text-[12px] font-bold uppercase tracking-[0.12em] inline-flex items-center gap-1.5 transition-all hover:shadow-lg hover:-translate-y-0.5"
                style={{
                  background: "linear-gradient(135deg, #1E5FEB 0%, #1849C8 100%)",
                  boxShadow: "0 6px 18px rgba(30, 95, 235, 0.32)",
                }}
                data-testid="tyro-retry-btn"
              >
                <ArrowCounterClockwise size={13} weight="bold" />
                Retry
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
